import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    Paper,
    Chip,
    Tooltip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import {
    Psychology as PsychologyIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

const ExpressionBrowser = ({ onLoadExpression, currentExpression, isModal, open, onClose }) => {
    const [expressions, setExpressions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    const fetchExpressions = useCallback(async () => {
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(`${API_URL}/api/simulations/expression`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setExpressions(response.data);
        } catch (error) {
            console.error('Failed to fetch expressions:', error);
            setError('Failed to load expressions');
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchExpressions();
        }
    }, [isAuthenticated, fetchExpressions]);

    const handleDeleteExpression = async (expressionId, event) => {
        event.stopPropagation();
        
        if (!window.confirm('Are you sure you want to delete this expression?')) {
            return;
        }

        try {
            const token = await getAccessTokenSilently();
            await axios.delete(`${API_URL}/api/simulations/expression/${expressionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Remove from local state
            setExpressions(prev => prev.filter(exp => exp._id !== expressionId));
        } catch (error) {
            console.error('Failed to delete expression:', error);
            setError('Failed to delete expression');
        }
    };

    const formatExpressionSummary = (expression) => {
        const expressionData = expression.expression || expression;
        if (!expressionData.expression || !Array.isArray(expressionData.expression)) {
            return 'No operators';
        }
        
        return expressionData.expression.map(op => op.name || 'Unknown').join(' + ');
    };

    const getOperatorCount = (expression) => {
        const expressionData = expression.expression || expression;
        if (!expressionData.expression || !Array.isArray(expressionData.expression)) {
            return 0;
        }
        return expressionData.expression.length;
    };

    if (loading) {
        const loadingContent = (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Loading expressions...
                </Typography>
            </Box>
        );
        
        if (isModal) {
            return (
                <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                    <DialogTitle>Load Saved Expression</DialogTitle>
                    <DialogContent>{loadingContent}</DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            );
        }
        
        return loadingContent;
    }

    if (error) {
        const errorContent = (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
        
        if (isModal) {
            return (
                <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                    <DialogTitle>Load Saved Expression</DialogTitle>
                    <DialogContent>{errorContent}</DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            );
        }
        
        return errorContent;
    }

    const renderContent = () => (
        <Box sx={{ height: isModal ? 400 : '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <PsychologyIcon sx={{ mr: 1 }} />
                    My Expressions ({expressions.length})
                </Typography>
            </Box>
            
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {expressions.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No saved expressions found.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Create and save an expression to see it here.
                        </Typography>
                    </Box>
                ) : (
                    <List>
                        {expressions.map((expression) => (
                            <ListItem
                                key={expression._id}
                                button
                                onClick={() => onLoadExpression(expression)}
                                sx={{ 
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <ListItemIcon>
                                    <PsychologyIcon color="primary" />
                                </ListItemIcon>
                                
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1">
                                                {(expression.expression && expression.expression.name) || expression.name || 'Unnamed Expression'}
                                            </Typography>
                                            <Chip 
                                                label={`${getOperatorCount(expression)} operators`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                {formatExpressionSummary(expression)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ScheduleIcon fontSize="small" color="action" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {expression.date_created ? 
                                                        new Date(expression.date_created).toLocaleDateString() :
                                                        'Unknown date'
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                />
                                
                                <ListItemSecondaryAction>
                                    <Tooltip title="Delete Expression">
                                        <IconButton 
                                            edge="end" 
                                            onClick={(e) => handleDeleteExpression(expression._id, e)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );

    if (isModal) {
        return (
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Load Saved Expression
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {renderContent()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {renderContent()}
        </Paper>
    );
};

export default ExpressionBrowser;
